import { lazy, Suspense } from "react";
import type { RouteObject } from "react-router-dom";
import i18n from "../i18n";

// Loading component for suspense fallback
const PageLoader = () => (
  <div className="min-h-screen bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950 flex items-center justify-center">
    <div className="text-center">
      <div className="w-12 h-12 border-4 border-cyan-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
      <p className="text-gray-400 text-sm">{i18n.t('loading')}</p>
    </div>
  </div>
);

// Lazy load all pages for better performance
const Home = lazy(() => import("../pages/home/page"));
const Shop = lazy(() => import("../pages/shop/page"));
const Inspect = lazy(() => import("../pages/inspect/page"));
const Checkout = lazy(() => import("../pages/checkout/page"));
const Admin = lazy(() => import("../pages/admin/page"));
const Login = lazy(() => import("../pages/login/page"));
const AiAssistant = lazy(() => import("../pages/ai-assistant/page"));
const Wish = lazy(() => import("../pages/wish/page"));
const BuildYourDream = lazy(() => import("../pages/byd/page"));
const LoadoutList = lazy(() => import("../pages/loadout-list/page"));
const Loadout = lazy(() => import("../pages/loadout/page"));
const NotFound = lazy(() => import("../pages/NotFound"));

// Info pages
const Shipping = lazy(() => import("../pages/info/shipping"));
const Terms = lazy(() => import("../pages/info/terms"));
const AboutUs = lazy(() => import("../pages/info/about-us"));

// Payment result pages
const PaymentSuccess = lazy(() => import("../pages/payment/success"));
const PaymentFail = lazy(() => import("../pages/payment/fail"));

// Wrap component with Suspense
const withSuspense = (Component: React.LazyExoticComponent<() => React.ReactElement>) => (
  <Suspense fallback={<PageLoader />}>
    <Component />
  </Suspense>
);

const routes: RouteObject[] = [
  {
    path: "/",
    element: withSuspense(Home),
  },
  {
    path: "/shop",
    element: withSuspense(Shop),
  },
  {
    path: "/product/:slug",
    element: withSuspense(Inspect),
  },
  {
    path: "/inspect/:id",
    element: withSuspense(Inspect),
  },
  {
    path: "/checkout",
    element: withSuspense(Checkout),
  },
  {
    path: "/admin",
    element: withSuspense(Admin),
  },
  {
    path: "/login",
    element: withSuspense(Login),
  },
  {
    path: "/ai-assistant",
    element: withSuspense(AiAssistant),
  },
  {
    path: "/wish",
    element: withSuspense(Wish),
  },
  {
    path: "/byd",
    element: withSuspense(BuildYourDream),
  },
  {
    path: "/loadouts",
    element: withSuspense(LoadoutList),
  },
  {
    path: "/loadout/:id",
    element: withSuspense(Loadout),
  },
  // Info pages
  {
    path: "/shipping",
    element: withSuspense(Shipping),
  },
  {
    path: "/terms",
    element: withSuspense(Terms),
  },
  {
    path: "/about",
    element: withSuspense(AboutUs),
  },
  // Payment result pages
  {
    path: "/payment/success",
    element: withSuspense(PaymentSuccess),
  },
  {
    path: "/payment/fail",
    element: withSuspense(PaymentFail),
  },
  {
    path: "*",
    element: withSuspense(NotFound),
  },
];

export default routes;
